import {
  isToday,
  isYesterday,
  addMonths,
  getUnixTime,
  subMinutes,
  format,
  startOfDay,
  subMonths,
  startOfMonth,
  eachDayOfInterval,
  eachMonthOfInterval,
  differenceInDays,
  differenceInMonths,
  isBefore,
  formatDistanceToNow as formatDistanceToNowDateFns,
  formatDistanceToNowStrict,
  type Locale,
  isPast,
  differenceInHours,
  differenceInYears,
  intervalToDuration,
  isExists,
} from 'date-fns';

export function intervalToDurationFromTo({
  from = new Date(),
  to = new Date(),
}: {
  from?: Date;
  to?: Date;
}) {
  return intervalToDuration({ start: from, end: to });
}

export function differenceInHoursFromTo({
  from = new Date(),
  to = new Date(),
}: {
  from?: Date;
  to?: Date;
}) {
  return differenceInHours(from, to);
}

export function differenceInDaysFromTo({
  from = new Date(),
  to = new Date(),
}: {
  from?: string | Date;
  to?: string | Date;
}) {
  return differenceInDays(
    typeof to === 'string' ? Date.parse(to) : to,
    typeof from === 'string' ? Date.parse(from) : from
  );
}

export function differenceInMonthsFromTo({
  from = new Date(),
  to = new Date(),
}: {
  from?: Date;
  to?: Date;
}) {
  return differenceInMonths(to, from);
}

export function incrementDays({
  incrementBy,
  baseDate = new Date(),
}: {
  incrementBy: number;
  baseDate?: Date;
}) {
  const baseDateLocal = new Date(baseDate);

  if (incrementBy === 0) {
    return baseDateLocal;
  }

  return new Date(baseDateLocal.setDate(baseDateLocal.getDate() + incrementBy));
}

export function decrementDays({
  decrementBy,
  baseDate = new Date(),
}: {
  decrementBy: number;
  baseDate?: Date;
}) {
  const baseDateLocal = new Date(baseDate);

  if (decrementBy === 0) {
    return baseDateLocal;
  }

  return new Date(baseDateLocal.setDate(baseDateLocal.getDate() - decrementBy));
}

export function incrementMonths({
  incrementBy,
  baseDate = new Date(),
}: {
  incrementBy: number;
  baseDate?: number | Date;
}) {
  const baseDateLocal = new Date(baseDate);

  if (incrementBy === 0) {
    return baseDateLocal;
  }

  return addMonths(baseDate, incrementBy);
}

export function decrementMonths({
  decrementBy,
  baseDate = new Date(),
}: {
  decrementBy: number;
  baseDate?: number | Date;
}) {
  const baseDateLocal = new Date(baseDate);

  if (decrementBy === 0) {
    return baseDateLocal;
  }

  return subMonths(baseDate, decrementBy);
}

export function isTodayOrYesterday(date: number | Date) {
  if (isToday(date)) {
    return 'Today';
  }
  if (isYesterday(date)) {
    return 'Yesterday';
  }
  return false;
}

export function getToday() {
  return new Date();
}

/*
  This function creates a UNIX timestamp representation of a local date. E.g. 3pm UTC can be
  5pm in some other time-zone the provided date uses. This function will return a timestamp, which evaluates to
  5pm UTC.
 */
export function getUTCTimestampInSeconds(date: Date) {
  const diff = date.getTimezoneOffset();
  return getUnixTime(subMinutes(date, diff));
}

/*
  This function creates a Date instance representation of a local date. E.g. 3pm UTC can be
  5pm in some other time-zone the provided date uses. This function will return a Date instance, which evaluates to
  3pm in the local timezone.
 */
export function getUTCDate(dateOrUNIXTimestamp: Date | number) {
  const dateLocal =
    typeof dateOrUNIXTimestamp === 'number'
      ? new Date(dateOrUNIXTimestamp * 1000)
      : dateOrUNIXTimestamp;

  return new Date(
    dateLocal.getUTCFullYear(),
    dateLocal.getUTCMonth(),
    dateLocal.getUTCDate(),
    dateLocal.getUTCHours(),
    dateLocal.getUTCMinutes(),
    dateLocal.getUTCSeconds()
  );
}

export function getStartOfDay(date: Date) {
  return startOfDay(date);
}

export function getStartOfMonth(date: Date) {
  return startOfMonth(date);
}

export function isFirstDayOfMonth(date: Date) {
  return date.getDate() === 1;
}

export function getEachDayOfInterval({
  start,
  end,
}: {
  start: Date;
  end: Date;
}) {
  return eachDayOfInterval({ start, end });
}

export function getEachMonthOfInterval({
  start,
  end,
}: {
  start: Date;
  end: Date;
}) {
  return eachMonthOfInterval({ start, end });
}

export function dateRangeToString({
  dateFormat,
  startDateOrUNIXTimestamp,
  endDateOrUNIXTimestamp,
}: {
  dateFormat: string;
  startDateOrUNIXTimestamp?: Date | number;
  endDateOrUNIXTimestamp?: Date | number;
}) {
  const fromFormatted = startDateOrUNIXTimestamp
    ? format(getUTCDate(startDateOrUNIXTimestamp), dateFormat)
    : '';
  const toFormatted = endDateOrUNIXTimestamp
    ? format(getUTCDate(endDateOrUNIXTimestamp), dateFormat)
    : '';
  const divider = fromFormatted && toFormatted ? '-' : '';

  return `${fromFormatted} ${divider} ${toFormatted}`.trim();
}

export function formatDistanceToNow({
  date,
  locale,
  strict = true,
}: {
  date: Date;
  locale?: Locale;
  strict?: boolean;
}) {
  const formatterFunction = strict
    ? formatDistanceToNowStrict
    : formatDistanceToNowDateFns;

  return formatterFunction(date, {
    addSuffix: true,
    locale,
  });
}

export function getDateFormat(location: string) {
  return location === 'us' ? 'MM/dd/yyyy' : 'dd/MM/yyyy';
}

export function getFormattedDate(date: Date, location: string) {
  const dateFormat = getDateFormat(location);
  return format(date, dateFormat);
}

export function getIsBefore({
  firstDate,
  secondDate,
}: {
  firstDate: Date;
  secondDate: Date;
}) {
  return isBefore(firstDate, secondDate);
}

export function getIsPast(date: Date) {
  return isPast(date);
}

/**
 * Turns the day/month/year a user gave us into a date, or null when those parts
 * don't describe one — an out of range day, or a day that doesn't exist in that
 * month. Missing parts are also null, so callers that treat a half filled form
 * as still valid need to check for that themselves.
 */
export function parseDateParts({
  day,
  month,
  year,
}: {
  day?: string | number;
  month?: string | number;
  year?: string | number;
}): Date | null {
  const dayNumber = Number(day);
  const monthNumber = Number(month);
  const yearNumber = Number(year);

  if (!dayNumber || !monthNumber || !yearNumber) {
    return null;
  }

  if (!isExists(yearNumber, monthNumber - 1, dayNumber)) {
    return null;
  }

  return new Date(yearNumber, monthNumber - 1, dayNumber);
}

export function parseFormattedDate(
  value: string,
  location: string
): Date | null {
  const [first, second, year, ...rest] = value
    .trim()
    .split(/\D+/)
    .filter(Boolean);

  if (rest.length > 0 || year?.length !== 4) {
    return null;
  }

  const isMonthFirst = getDateFormat(location).startsWith('MM');

  return parseDateParts({
    day: isMonthFirst ? second : first,
    month: isMonthFirst ? first : second,
    year,
  });
}

export function getAgeFromDate(dob: Date) {
  return differenceInYears(new Date(), dob);
}
